/* @HEADER
 * @version 1.1.121 | 2026-10-16
 * @purpose The shell's entitlement gate that AppShell held inline: which tools the plan allows, the route and view redirects, the space-creation limit, the read-only delete guard, and the shared upsell placard's state. Same behaviour, its own file.
 * @sidecar useEntitlementGate.ts.header.md
 * @layer app | @hex _none_ | @ctx entitlement
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7422, ZVX-DEV-2451, ZVX-DEV-2468, ZVX-DEV-2469

import { useState, useEffect } from 'react';
import type { Route, SpaceView } from './routes';
import { useMessages } from '../shared/i18n/useMessages';
import { isEntitlementEnforcementEnabled } from '../shared/featureFlags';
import {
  useEntitlement,
  isFeatureAllowed,
  ROUTE_FEATURE_MAP,
  SPACE_VIEW_FEATURE_MAP,
  useResourceLimit,
  useIsReadOnly,
} from '../features/entitlement/public-api';
import { isAggregatingSpace } from '@modules/board/public-api.mjs';
import type { BoardGroupRecord } from '../lib/db';

/**
 * ZVX-DEV-7422 — WHY THIS FILE EXISTS. Campaign «god-file split», target
 * AppShell.tsx. Everything the shell decided about the plan lived in one
 * stretch of it and fed four places in the markup; it moves here whole, and
 * the shell reads the returned object. The code moved; the behaviour did not.
 */

export interface EntitlementGateOptions<T extends { route: Route }> {
  /** The sidebar's tools; the ones the plan hides are filtered out. */
  navItems: readonly T[];
  boardGroups: readonly BoardGroupRecord[];
  currentRoute: Route;
  activeView?: SpaceView;
  onNavigate: (route: Route) => void;
  onSwitchView?: (view: SpaceView) => void;
  onDeleteBoardGroup?: (groupId: string) => void;
}

export interface EntitlementUpsell {
  open: boolean;
  /** Override copy for a creation limit or read-only; absent for a feature gate. */
  title?: string;
  body?: string;
  openFeature: () => void;
  openSpaceLimit: () => void;
  close: () => void;
}

export interface EntitlementGate<T> {
  canvasAllowed: boolean;
  wikiAllowed: boolean;
  crossProjectAllowed: boolean;
  gatedNavItems: T[];
  anyToolHidden: boolean;
  isReadOnly: boolean;
  canCreateSpace: boolean;
  /** Absent exactly when `onDeleteBoardGroup` is. */
  guardedDeleteBoardGroup?: (groupId: string) => void;
  upsell: EntitlementUpsell;
}

export function useEntitlementGate<T extends { route: Route }>({
  navItems,
  boardGroups,
  currentRoute,
  activeView,
  onNavigate,
  onSwitchView,
  onDeleteBoardGroup,
}: EntitlementGateOptions<T>): EntitlementGate<T> {
  const m = useMessages();

  // ── ZVX-DEV-2451 (S4, epic ZVX-DEV-2447): feature-visibility gate ──
  // A tool whose feature id is absent from the resolved entitlement's
  // featureFlags is hidden from the sidebar/nav entirely and replaced by one
  // consistent upsell affordance (no per-tool bespoke copy in V1). Direct
  // hash navigation to a gated Route/SpaceView is redirected to the same
  // safe default the sidebar enforces (no back-door access via URL).
  const entitlement = useEntitlement();
  const enforcementOn = isEntitlementEnforcementEnabled();
  const canvasAllowed = isFeatureAllowed(
    ROUTE_FEATURE_MAP.canvas,
    entitlement.featureFlags,
    enforcementOn,
  );
  const wikiAllowed = isFeatureAllowed(
    ROUTE_FEATURE_MAP.wiki,
    entitlement.featureFlags,
    enforcementOn,
  );
  const crossProjectAllowed = isFeatureAllowed(
    'cross_project',
    entitlement.featureFlags,
    enforcementOn,
  );
  const gatedNavItems = navItems.filter((item) =>
    isFeatureAllowed(ROUTE_FEATURE_MAP[item.route], entitlement.featureFlags, enforcementOn),
  );
  const hiddenNavItems = navItems.filter(
    (item) =>
      !isFeatureAllowed(ROUTE_FEATURE_MAP[item.route], entitlement.featureFlags, enforcementOn),
  );
  const anyToolHidden =
    !canvasAllowed || !wikiAllowed || !crossProjectAllowed || hiddenNavItems.length > 0;
  const [upsellOpen, setUpsellOpen] = useState(false);

  // ZVX-DEV-2468 (S11): space-creation limit. Aggregating/system spaces
  // («Все проекты»/«Все задачи»/«Входящие») do NOT count toward `spaceLimit`.
  // `useResourceLimit` reads the enforcement kill-switch, so a disabled
  // enforcement or a null (unlimited) limit both resolve to "allowed".
  const realSpaceCount = boardGroups.filter((g) => !isAggregatingSpace(g)).length;
  // ZVX-DEV-2469 (S12): read-only after grace also blocks space creation --
  // combined here (not forked into a second gate) so BoardNav's single
  // `canCreateSpace` boolean already reflects both reasons a create can be
  // blocked. `isReadOnly` holds identically online/offline (see
  // useEntitlementLifecycle.ts) and itself already respects the kill-switch.
  const isReadOnly = useIsReadOnly();
  const isWithinSpaceLimit = useResourceLimit(realSpaceCount, entitlement.spaceLimit);
  const canCreateSpace = !isReadOnly && isWithinSpaceLimit;
  // The shared upsell placard shows feature-gate copy by default; a creation
  // limit (or read-only) passes a title/body override so the same surface
  // explains the actual block reason.
  const [upsellCopy, setUpsellCopy] = useState<{ title?: string; body?: string }>({});
  const openFeatureUpsell = () => {
    setUpsellCopy({});
    setUpsellOpen(true);
  };
  const openSpaceLimitUpsell = () => {
    if (isReadOnly) {
      setUpsellCopy({ title: m.entitlement.readOnly.title, body: m.entitlement.readOnly.message });
      setUpsellOpen(true);
      return;
    }
    setUpsellCopy({ title: m.entitlement.limit.space.title, body: m.entitlement.limit.space.body });
    setUpsellOpen(true);
  };
  // ZVX-DEV-2469 (S12): space DELETE is a mutation too -- guard it the same
  // way as create, silently no-op while read-only (the persistent
  // non-dismissible read-only banner already explains why).
  const guardedDeleteBoardGroup = onDeleteBoardGroup
    ? (groupId: string) => {
        if (isReadOnly) return;
        onDeleteBoardGroup(groupId);
      }
    : undefined;

  // Route-level guard: a direct hash navigation (or stale in-memory route) to
  // a Route the current entitlement no longer allows redirects to 'tasks'.
  useEffect(() => {
    if (
      !isFeatureAllowed(ROUTE_FEATURE_MAP[currentRoute], entitlement.featureFlags, enforcementOn)
    ) {
      onNavigate('tasks');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, entitlement.featureFlags, enforcementOn]);

  // SpaceView-level guard: 'gantt'/'outline' redirect to the default 'kanban'
  // view within the current space when not allowed.
  useEffect(() => {
    if (
      activeView &&
      onSwitchView &&
      !isFeatureAllowed(SPACE_VIEW_FEATURE_MAP[activeView], entitlement.featureFlags, enforcementOn)
    ) {
      onSwitchView('kanban');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, entitlement.featureFlags, enforcementOn]);

  return {
    canvasAllowed,
    wikiAllowed,
    crossProjectAllowed,
    gatedNavItems,
    anyToolHidden,
    isReadOnly,
    canCreateSpace,
    guardedDeleteBoardGroup,
    upsell: {
      open: upsellOpen,
      title: upsellCopy.title,
      body: upsellCopy.body,
      openFeature: openFeatureUpsell,
      openSpaceLimit: openSpaceLimitUpsell,
      close: () => setUpsellOpen(false),
    },
  };
}
